// ###### IMPORTS
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ListBulletIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import { createPortal } from 'react-dom';

// ###### CONSTANTS
const VIEW_MODE_OPTIONS = [
  { value: 'title-only', label: 'Title Only', icon: ListBulletIcon },
  { value: 'preview', label: 'Preview', icon: DocumentTextIcon }
];

// ###### COMPONENT
const DropdownViewMode = ({ 
  viewMode = 'preview',
  onSelect = () => {},
  width = 'w-full',
  disabled = false,
  usePortal = false,
  portalRef = null
}) => {
  // ###### STATES
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Get selected option
  const selectedOption = VIEW_MODE_OPTIONS.find(opt => opt.value === viewMode) || VIEW_MODE_OPTIONS[1];
  const SelectedIcon = selectedOption.icon;

  // ###### EFFECTS
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        (buttonRef.current && buttonRef.current.contains(event.target)) ||
        (dropdownRef.current && dropdownRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, usePortal]);

  // Função para calcular estilo do Portal
  const getPortalStyle = () => {
    const ref = portalRef && portalRef.current ? portalRef : buttonRef;
    if (!ref.current) return {};
    const rect = ref.current.getBoundingClientRect();
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      zIndex: 20,
      minWidth: rect.width,
    };
  };

  // ###### HANDLERS
  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleSelect = (value) => {
    onSelect(value);
    setIsOpen(false);
  };

  // ###### RENDER
  return (
    <div className="relative" ref={buttonRef}>
      {/* Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          inline-flex justify-between items-center ${width} rounded-md border 
          bg-white border-gray-300 px-3 py-2 text-sm text-gray-700 
          hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-1 
          focus:ring-blue-500 focus:border-blue-500 transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}
        `}
      >
        <div className="flex items-center gap-2">
          <SelectedIcon className="h-4 w-4 text-gray-500" />
          <span className="truncate">
            {selectedOption.label}
          </span>
        </div>
        <ChevronDownIcon 
          className={`ml-1 h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        usePortal
          ? createPortal(
              <div style={getPortalStyle()}>
                <div
                  ref={dropdownRef}
                  className={`rounded-md bg-white shadow-lg border border-gray-200 max-h-60 overflow-y-auto z-10 ${width}`}
                >
                  {VIEW_MODE_OPTIONS.map((option) => {
                    const OptionIcon = option.icon;
                    const isSelected = viewMode === option.value;
                    return (
                      <div
                        key={option.value}
                        onClick={() => handleSelect(option.value)}
                        className={`
                          cursor-pointer px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2
                          ${isSelected ? 'text-green-600 bg-green-50' : 'text-gray-700'}
                        `}
                      >
                        <input
                          type="radio"
                          checked={isSelected}
                          readOnly
                          className="accent-green-400"
                        />
                        <OptionIcon className="h-4 w-4" />
                        {option.label}
                      </div>
                    );
                  })}
                </div>
              </div>,
              document.body
            )
          : (
            <div
              ref={dropdownRef}
              className={`absolute top-full right-0 z-10 mt-1 ${width} rounded-md bg-white shadow-lg border border-gray-200 max-h-60 overflow-y-auto`}
            >
              {VIEW_MODE_OPTIONS.map((option) => {
                const OptionIcon = option.icon;
                const isSelected = viewMode === option.value;
                return (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={`
                      cursor-pointer px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2
                      ${isSelected ? 'text-green-600 bg-green-50' : 'text-gray-700'}
                    `}
                  >
                    <input
                      type="radio"
                      checked={isSelected}
                      readOnly
                      className="accent-green-400"
                    />
                    <OptionIcon className="h-4 w-4" />
                    {option.label}
                  </div>
                );
              })}
            </div>
          )
      )}
    </div>
  );
};

export default DropdownViewMode;
